import React from "react";
import { Shield, FileCheck, CheckCircle, MapPin, Truck, Car } from "lucide-react";
import { Link } from "gatsby";

interface ServiceCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
  link: string;
  imageUrl: string;
  imageAlt: string;
}

const ServiceCard: React.FC<ServiceCardProps> = ({
  icon: Icon,
  title,
  description,
  link,
  imageUrl,
  imageAlt,
}) => (
  <Link to={link} className="block">
    <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform hover:scale-[1.02] h-full flex flex-col">
      <div className="relative h-52">
        <picture>
          {imageUrl.includes("cleared-background-check-512") ? (
            <>
              <source
                srcSet="/images/cleared-background-check-512.avif"
                type="image/avif"
              />
              <source
                srcSet="/images/cleared-background-check-512.webp"
                type="image/webp"
              />
              <source
                srcSet="/images/cleared-background-check-512.png"
                type="image/png"
              />
              <img
                src="/images/cleared-background-check-512.png"
                alt={imageAlt}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </>
          ) : (
            <>
              <source srcSet={imageUrl} type="image/jpeg" />
              <img
                src={imageUrl}
                alt={imageAlt}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </>
          )}
        </picture>
      </div>
      <div className="p-6 flex-grow flex flex-col">
        <div className="flex items-center mb-4">
          <Icon className="w-6 h-6 text-primary mr-3 flex-shrink-0" />
          <h3 className="text-lg font-bold">{title}</h3>
        </div>
        <p className="text-sm text-gray-600 flex-grow">{description}</p>
      </div>
    </div>
  </Link>
);

const ProfessionsList: React.FC = () => {
  const services = [
    {
      icon: Shield,
      title: "Security Guard License CRJMC",
      description:
        "The same standardized CRJMC check required for your Ontario Security Guard License application or renewal. Fast 15-minute processing with our authorized service.",
      link: "/ontario/security-guards",
      imageUrl:
        "/images/security-guard/security-guard-with-background-check-cleared-big.jpg",
      imageAlt: "Security guard with cleared background check",
    },
    {
      icon: FileCheck,
      title: "Private Investigator License CRJMC",
      description:
        "Identical CRJMC check format accepted for Ontario Private Investigator License applications and renewals. Same fast processing, same comprehensive check.",
      link: "/ontario/private-investigators",
      imageUrl:
        "/images/private-investigator/cleared-background-check-private-investigator-512.jpg",
      imageAlt: "Private investigator with cleared background check",
    },
    {
      icon: MapPin,
      title: "Real Estate Agent CRJMC",
      description:
        "The same CRJMC check format required for RECO registration and real estate agent license applications. Fast online processing for new registrations and renewals.",
      link: "/ontario/real-estate-agents",
      imageUrl:
        "/images/real-estate-agent/cleared-background-check-real-estate-agent-512.jpg",
      imageAlt: "Real estate agent with cleared background check",
    },
    {
      icon: Car,
      title: "Uber Drivers CRJMC",
      description:
        "The same CRJMC check format required for Uber and Uber Eats driver background checks. Fast online processing for both new applications and annual verifications.",
      link: "/ontario/uber",
      imageUrl:
        "/images/uber-driver/uber-driver-background-check-512.jpg",
      imageAlt: "Uber driver with cleared background check",
    },
    {
      icon: Truck,
      title: "Tow Operators CRJMC",
      description:
        "The same CRJMC check format required for Ontario Tow Operators certification. Fast online processing for both new applications and certification renewals.",
      link: "/ontario/tow-operators",
      imageUrl:
        "/images/tow-operator/tow-operator-with-background-check-cleared.jpg",
      imageAlt: "Tow operator with cleared background check",
    },
  ];

  return (
    <div
      id="professions-list"
      className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8"
    >
      {services.map((service, index) => (
        <ServiceCard key={index} {...service} />
      ))}
    </div>
  );
};

export default ProfessionsList;
